import { useQuery } from '@tanstack/react-query';
import { type ReactElement, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { queryOrganisations } from '@/features/organisations/data';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects/hooks/projectHooks';
import { useProjectsQuery } from '@/features/projects/hooks/projects';
import { getLoadingMessage } from '@/shared/copy/loading';
import { queryKeys } from '@/shared/hooks';
import { raisedPanelSx } from '@/shared/ui/panelShadowStyles';
import { s } from '@/theme/tokens';
import { EmptyState, Panel } from '@/ui/composites';
import { SettingsPageLoadingState } from '@/ui/composites/SettingsPageLoadingState';
import { Section } from '@/ui/layout/Section';
import { Stack } from '@/ui/layout/Stack';
import { Box, PageShell, Text } from '@/ui/primitives';
import { ProjectSettingsPage } from './ProjectSettingsPage';
import type { AuthSession } from '@/auth/types';
import type { OrganisationDto, ProjectDto } from '@flowqa/shared';

type ProjectSettingsSlugRedirectProps = {
  session: AuthSession | null;
};

const findBySlugOrId = <TItem extends { id: string; slug: string }>(
  items: TItem[],
  slugOrId: string | undefined,
): TItem | null => {
  if (!slugOrId) return null;
  return items.find((item) => item.slug === slugOrId || item.id === slugOrId) ?? null;
};

const renderEmptyState = (title: string, body?: string): ReactElement => (
  <PageShell maxWidth="xs" paddingX="lg" paddingY="xl" paddingBottom="xl">
    <EmptyState title={title} body={body} />
  </PageShell>
);

const renderLoadError = (title: string, message: string): ReactElement => (
  <PageShell maxWidth="xs" paddingX="lg" paddingY="xl" paddingBottom="xl">
    <Stack gap="xl" sx={{ pt: s('5xl') }}>
      <Section>
        <Box sx={{ width: '100%' }}>
          <Panel sx={raisedPanelSx}>
            <Stack
              gap="lg"
              sx={{
                width: '100%',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              <Text role="pageTitle">{title}</Text>
              <Text role="bodyMuted">{message}</Text>
            </Stack>
          </Panel>
        </Box>
      </Section>
    </Stack>
  </PageShell>
);

export const ProjectSettingsSlugRedirect = ({
  session,
}: ProjectSettingsSlugRedirectProps): ReactElement => {
  const { workspaceSlug, projectSlug } = useParams<{
    workspaceSlug: string;
    projectSlug: string;
  }>();
  const { setSelectedOrganisationId } = useOrganisationFilter();
  const { setSelectedProjectId } = useProjectFilter();
  const organisationsQuery = useQuery({
    queryKey: queryKeys.organisations(session?.userId ?? null),
    enabled: Boolean(session?.tokens.accessToken),
    queryFn: async (): Promise<OrganisationDto[]> => {
      const result = await queryOrganisations(session?.tokens.accessToken ?? null);
      return result.items;
    },
    staleTime: 30_000,
  });
  const organisation = useMemo(
    () => findBySlugOrId(organisationsQuery.data ?? [], workspaceSlug),
    [organisationsQuery.data, workspaceSlug],
  );
  const projectsQuery = useProjectsQuery({
    enabled: organisation !== null,
    organisationId: organisation?.id ?? null,
    refetchOnWindowFocus: true,
  });
  const project = useMemo<ProjectDto | null>(
    () => findBySlugOrId(projectsQuery.data?.items ?? [], projectSlug),
    [projectSlug, projectsQuery.data?.items],
  );

  useEffect(() => {
    if (!organisation || !project) return;
    setSelectedOrganisationId(organisation.id);
    setSelectedProjectId(project.id);
  }, [organisation, project, setSelectedOrganisationId, setSelectedProjectId]);

  if (!session) {
    return renderEmptyState('Sign in to manage project settings.');
  }

  if (!workspaceSlug || !projectSlug) {
    return renderEmptyState('Project settings link is incomplete.');
  }

  if (organisationsQuery.isPending || (organisation !== null && projectsQuery.isPending)) {
    return <SettingsPageLoadingState message={getLoadingMessage('projectSettings')} />;
  }

  if (organisationsQuery.error) {
    return renderLoadError('Unable to load workspaces', organisationsQuery.error.message);
  }

  if (organisation === null) {
    return renderEmptyState('Workspace not found');
  }

  if (projectsQuery.error) {
    return renderLoadError('Unable to load projects', projectsQuery.error.message);
  }

  if (project === null) {
    return renderEmptyState('Project not found in this workspace');
  }

  return <ProjectSettingsPage session={session} routeProjectId={project.id} />;
};
